import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { X, Search, ChevronDown } from 'lucide-react';
import { Spinner, useToast } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { registerRiderPayment } from '../../services/api/riders/riders';
import { appColors } from '../../utils/colors';
import { riderPaymentSchema } from '../../schemas/payment';

const methods = ['NEQUI', 'DAVIPLATA', 'BANCOLOMBIA', 'EFECTIVO'];

const RiderPaymentModal = ({
	isOpen,
	onClose,
	riders,
	preselectedRiderId = null,
}) => {
	const [submitLoading, setSubmitLoading] = useState(false);
	const [rider, setRider] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownRef = useRef(null);
	const business = useSelector(({ auth }) => auth.business);
	const toast = useToast();

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
	} = useForm({
		resolver: yupResolver(riderPaymentSchema),
		defaultValues: {
			rider: '',
			partner: business?.id || '',
			method: '',
			amount: '',
			details: '',
		},
	});

	useEffect(() => {
		if (!isOpen) {
			reset();
			setRider(null);
			setSearchTerm('');
			setIsDropdownOpen(false);
		}
	}, [isOpen, reset]);

	useEffect(() => {
		if (isOpen && preselectedRiderId && riders && riders.length > 0) {
			const preselectedRider = riders.find((r) => r.id === preselectedRiderId);
			if (preselectedRider) {
				setRider(preselectedRider);
				setSearchTerm(preselectedRider.name);
				setValue('rider', preselectedRider.id);
			}
		}
	}, [isOpen, preselectedRiderId, riders, setValue]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const onSubmit = async (data) => {
		if (!rider) {
			toast({
				title: 'Error',
				description: 'Debes seleccionar un operador',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
			return;
		}

		try {
			setSubmitLoading(true);
			const multiplier = data.type === 'send' ? -1 : 1;
			const paymentData = {
				rider: rider.id,
				partner: business?.id || '',
				method: data.method,
				amount: parseFloat(data.amount) * multiplier,
				details: data.details || '',
			};

			await registerRiderPayment(rider.id, paymentData);

			toast({
				title: 'Pago registrado',
				description: 'El pago se ha registrado correctamente',
				status: 'success',
				duration: 5000,
				isClosable: true,
			});

			reset();
			setRider(null);
			setSearchTerm('');
			onClose();
		} catch (error) {
			console.error(error);
			toast({
				title: 'Error',
				description: 'Ocurrió un error al registrar el pago',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		} finally {
			setSubmitLoading(false);
		}
	};

	const filteredRiders = riders.filter((r) => {
		const searchLower = searchTerm.toLowerCase();
		return (
			r.name?.toLowerCase().includes(searchLower) ||
			r.document?.toLowerCase().includes(searchLower) ||
			r.phone?.toLowerCase().includes(searchLower)
		);
	});

	const handleSelectRider = (selectedRider) => {
		setRider(selectedRider);
		setSearchTerm(selectedRider.name);
		setIsDropdownOpen(false);
		setValue('rider', selectedRider.id);
	};

	const handleInputChange = (e) => {
		setSearchTerm(e.target.value);
		setIsDropdownOpen(true);
		if (!e.target.value) {
			setRider(null);
		}
	};

	if (!isOpen) {
		return null;
	}

	return (
		<div
			className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black bg-opacity-50"
			style={{ transition: 'opacity 0.2s ease-in-out' }}
		>
			<div
				className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-lg flex flex-col relative z-[1101] max-h-[95vh] sm:max-h-[85vh]"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-white rounded-t-2xl sm:rounded-t-lg">
					<div className="flex items-center justify-between gap-3">
						<h2 className="text-lg sm:text-xl font-semibold text-gray-800 leading-tight">
							Registrar pago de operador
						</h2>
						<button
							onClick={onClose}
							className="p-1 rounded-md hover:bg-gray-100 transition-colors flex-shrink-0"
							aria-label="Cerrar modal"
						>
							<X size={22} className="text-gray-500" />
						</button>
					</div>
				</div>

				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col flex-1 min-h-0"
				>
					<div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
						<div className="relative" ref={dropdownRef}>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Seleccionar operador
							</label>
							<div className="relative">
								<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<Search size={18} className="text-gray-400" />
								</div>
								<input
									type="text"
									value={searchTerm}
									onChange={handleInputChange}
									onFocus={() => setIsDropdownOpen(true)}
									placeholder="Buscar por nombre, documento o teléfono..."
									className="w-full pl-10 pr-10 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
									disabled={submitLoading}
								/>
								<div className="absolute inset-y-0 right-0 pr-3 flex items-center">
									{submitLoading ? (
										<Spinner size="sm" color={appColors.primary} />
									) : (
										<ChevronDown
											size={18}
											className={`text-gray-400 transition-transform ${
												isDropdownOpen ? 'rotate-180' : ''
											}`}
										/>
									)}
								</div>
							</div>

							{isDropdownOpen && !submitLoading && (
								<div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 sm:max-h-60 overflow-y-auto">
									{filteredRiders.length > 0 ? (
										filteredRiders.map((r) => (
											<div
												key={r.id}
												onClick={() => handleSelectRider(r)}
												className="px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
											>
												<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
													<div className="min-w-0">
														<p className="text-sm font-medium text-gray-900 truncate">
															{r.name}
														</p>
														<p className="text-xs text-gray-500 truncate">
															{r.document_type}: {r.document}
														</p>
													</div>
													<div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 sm:gap-1 sm:text-right">
														<p className="text-xs text-gray-500">{r.phone}</p>
														<span
															className={`inline-block px-2 py-0.5 text-xs rounded-full ${
																r.status === 'active'
																	? 'bg-green-100 text-green-800'
																	: 'bg-red-100 text-red-800'
															}`}
														>
															{r.status === 'active' ? 'Activo' : 'Inactivo'}
														</span>
													</div>
												</div>
											</div>
										))
									) : (
										<div className="px-4 py-3 text-sm text-gray-500 text-center">
											No se encontraron operadores
										</div>
									)}
								</div>
							)}

							{rider && (
								<div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<p className="text-sm font-medium text-gray-900">
												Operador seleccionado:
											</p>
											<p className="text-sm text-gray-700 mt-1 break-words">
												{rider.name} - {rider.document_type}: {rider.document}
											</p>
										</div>
										<button
											type="button"
											onClick={() => {
												setRider(null);
												setSearchTerm('');
												setValue('rider', '');
											}}
											className="p-1 rounded-md hover:bg-orange-100 transition-colors flex-shrink-0"
											aria-label="Deseleccionar operador"
										>
											<X size={18} className="text-orange-600" />
										</button>
									</div>
								</div>
							)}
							{errors.rider && (
								<p className="mt-1 text-sm text-red-600">
									{errors.rider.message}
								</p>
							)}
						</div>
					</div>

					<div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 min-h-0">
						{rider ? (
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Saldo actual <span className="text-red-500">*</span>
									</label>
									<p className="text-sm text-gray-700 mt-1 break-all">
										{rider.account}
									</p>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Monto <span className="text-red-500">*</span>
									</label>
									<input
										type="number"
										{...register('amount', { valueAsNumber: true })}
										step="0.01"
										min="0"
										placeholder="0.00"
										className={`w-full px-3 py-2.5 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
											errors.amount ? 'border-red-500' : 'border-gray-300'
										}`}
										disabled={submitLoading}
									/>
									{errors.amount && (
										<p className="mt-1 text-sm text-red-600">
											{errors.amount.message}
										</p>
									)}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Envía/recibe <span className="text-red-500">*</span>
									</label>
									<select
										{...register('type')}
										className={`w-full px-3 py-2.5 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
											errors.type ? 'border-red-500' : 'border-gray-300'
										}`}
										disabled={submitLoading}
									>
										<option value="">Selecciona una opción</option>
										<option value="send">Agus envía</option>
										<option value="receive">Operador envía</option>
									</select>
									{errors.type && (
										<p className="mt-1 text-sm text-red-600">
											{errors.type.message}
										</p>
									)}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Método de pago <span className="text-red-500">*</span>
									</label>
									<select
										{...register('method')}
										className={`w-full px-3 py-2.5 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
											errors.method ? 'border-red-500' : 'border-gray-300'
										}`}
										disabled={submitLoading}
									>
										<option value="">Selecciona un método de pago</option>
										{methods.map((method) => (
											<option key={method} value={method}>
												{method}
											</option>
										))}
									</select>
									{errors.method && (
										<p className="mt-1 text-sm text-red-600">
											{errors.method.message}
										</p>
									)}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Detalles (opcional)
									</label>
									<textarea
										{...register('details')}
										rows={3}
										placeholder="Agrega detalles adicionales sobre el pago..."
										className={`w-full px-3 py-2.5 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none ${
											errors.details ? 'border-red-500' : 'border-gray-300'
										}`}
										disabled={submitLoading}
									/>
									{errors.details && (
										<p className="mt-1 text-sm text-red-600">
											{errors.details.message}
										</p>
									)}
								</div>
							</div>
						) : (
							<div className="flex items-center justify-center py-10 sm:py-16">
								<p className="text-gray-500 text-center text-sm sm:text-base px-4">
									Selecciona un operador para continuar
								</p>
							</div>
						)}
					</div>

					<div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
						<div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
							<button
								type="button"
								onClick={onClose}
								disabled={submitLoading}
								className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
							>
								Cancelar
							</button>
							<button
								type="submit"
								disabled={submitLoading || !rider}
								className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
							>
								{submitLoading ? (
									<>
										<Spinner size="sm" color="white" />
										<span>Registrando...</span>
									</>
								) : (
									<span>Registrar pago</span>
								)}
							</button>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default RiderPaymentModal;
